/**
 * Indeling van de roadmap als boom (2026-09-27).
 *
 * - Kolom = tijdstap: de afstand tot de huidige situatie (de WP-main).
 * - Rij = een toekomstpad. Een plateau staat in de rij van z'n ouder, zodat een
 *   vervolgplateau direct áchter z'n voorganger komt; een tweede kind begint
 *   een nieuwe rij eronder.
 * - Zusters in volgorde van aanmaken: het oudste pad bovenaan.
 *
 * Tot deze datum stond elk plateau in een eigen kolom, in een rij. Twee zusters
 * (roadmap-tijdreizen.md §6.4: "roadmap A en roadmap B zijn twee paden") zagen
 * er dan uit als opeenvolgende stappen, en het vervolg van de ene zuster schoof
 * achter de andere.
 */

export interface RoadmapLaneLike {
  id: string;
  parentBranchId?: string;
  createdAt?: string;
}

export interface RoadmapCell<L> {
  lane: L;
  /** Positie in de oorspronkelijke lijst (0 = de huidige situatie). */
  idx: number;
  col: number;
  row: number;
}

export interface RoadmapLayout<L> {
  cells: RoadmapCell<L>[];
  cols: number;
  rows: number;
}

export function roadmapLayout<L extends RoadmapLaneLike>(lanes: readonly L[]): RoadmapLayout<L> {
  const idxOf = new Map(lanes.map((l, i) => [l.id, i]));
  const children = new Map<string, L[]>();
  const roots: L[] = [];
  for (const lane of lanes) {
    const parent = lane.parentBranchId;
    if (parent && idxOf.has(parent) && parent !== lane.id) {
      const list = children.get(parent) ?? [];
      list.push(lane);
      children.set(parent, list);
    } else {
      roots.push(lane);
    }
  }
  const byAge = (a: L, b: L) =>
    (a.createdAt ?? "").localeCompare(b.createdAt ?? "") ||
    (idxOf.get(a.id) ?? 0) - (idxOf.get(b.id) ?? 0);
  for (const list of children.values()) list.sort(byAge);
  // De huidige situatie (index 0) altijd als eerste wortel.
  roots.sort((a, b) =>
    (idxOf.get(a.id) ?? 0) === 0 ? -1 : (idxOf.get(b.id) ?? 0) === 0 ? 1 : byAge(a, b),
  );

  const cells: RoadmapCell<L>[] = [];
  const placed = new Set<string>();
  let cols = 0;
  // Geeft de eerstvolgende vrije rij terug.
  const place = (lane: L, col: number, row: number): number => {
    if (placed.has(lane.id)) return row;
    placed.add(lane.id);
    cells.push({ lane, idx: idxOf.get(lane.id) ?? 0, col, row });
    cols = Math.max(cols, col + 1);
    const kids = children.get(lane.id) ?? [];
    if (kids.length === 0) return row + 1;
    let next = row;
    for (const kid of kids) next = place(kid, col + 1, next);
    return next;
  };
  let row = 0;
  for (const root of roots) row = place(root, 0, row);
  // Wat door een cyclus niet bereikbaar was: los onderaan, zodat niets verdwijnt.
  for (const lane of lanes) if (!placed.has(lane.id)) row = place(lane, 0, row);

  return { cells, cols, rows: row };
}
